import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import useScript from '@/hooks/useScript';
import useSetupArtisticUrls from '@/hooks/useSetupArtisticUrls';

import * as S from './ProductBaseV2.styles';

import { ProductTypes, RouteProps } from '@/types/ecommerce.types';

import { RootState } from '@/store';
import { setShouldCrop } from '@/store/editor/editorSlice';
import { setTiles } from '@/store/upload/uploadSlice';
import DataService from '@/services/DataService';
import TrackingService from '@/services/TrackingService';
import {
  getCurrentVariant,
  getFrameSizeFromVariant,
  getMetafieldV2,
  getQueryParams,
  isPartnerMatch,
  isOrnamentProduct,
} from '@/utils/utils';

import { getArtisticHandle } from '../shared/artistic/getArtisticHandle';
import { removeArtisticTiles } from '../shared/artistic/removeArtisticTiles';

import ProductVariants from '../options/ProductVariants';
import ProductGallerySizes from '../options/ProductGallerySizes';
import ProductSwatches from '../options/ProductSwatches';
import Cart from '../cart/Cart';
import ProductCta from '../cart/ProductCta';
import Reviews from '../reviews/Reviews';
import Resolution from '../dialog/Resolution';
import ValueProps from '../valueProps/ValueProps';
import ProductOptions from '../options/MatOptions';
import ProductHallwaySize from '../options/ProductHallwaySize';
import RelatedProducts from '../relatedProducts/RelatedProducts';
import Performance from '../upload/Performance';
import GalleryDescription from '../description/GalleryDescription';
import GalleryItemsDescription from '../description/GalleryItemsDescription';
import Accordion from '../../templates/accordion/Accordion';
import AccordionContainer from '../../templates/accordion/AccordionContainer';
import ProductMedia from '../media/ProductMedia';
import { useMediaQuery } from 'react-responsive';
import ProductFaqV2 from '../faq/ProductFaqV2';
import ProductAccordionCustom from '../description/ProductAccordionCustom';
import { BuilderWrapper } from '../productLP/ProductLP.styles';
import Selector from '../options/Selector';
import ProductCollections from '../options/ProductCollections';
import FloatingCta from '../cart/FloatingCta';
import { SectionTypes } from '../types';
import ProductDescriptionV2 from '../description/ProductDescriptionV2';
import ProductHeaderV2 from '../header/ProductHeaderV2';
import ProductReviewMock2 from '../shared/ProductReviewMock2';
import ProductInfoV2 from '../header/ProductInfoV2';
import OrientationOptions from '../options/OrientationOptions';
import ProductAttributionDescriptionV2 from '../description/ProductAttributionDescriptionV2';
import { FrameBuilderStyled, FrameBuilderPodStyled } from './ProductBaseV2.styles';
import ProductCollectionGroup from '../options/ProductCollectionGroup';
import ProductImageSwatches from '../options/ProductImageSwatches';
import MatImageOptions from '../options/MatImageOptions';
import Editor from '../editor/Editor';
import GalleryMobileOverlay from '../slider/GalleryMobileOverlay';
import SizeGuide from '@/components/sizeGuide/SizeGuide';
import SizeGuideButton from '../sizeGuide/SizeGuideButton';
import { getPartner } from '@/utils/partner';
import BottomButtons, { Buttons } from '../frameBuilder/BottomButtons';
import ColorIcon from '@/components/templates/icons/ColorIcon';
import FrameIcon from '@/components/templates/icons/FrameIcon';
import ShapeIcon from '@/components/templates/icons/ShapeIcon';
import ProductIcon from '@/components/templates/icons/ProductIcon';
import GeneralIcon from '@/components/templates/icons/GeneralIcon';
import OrientationIcon from '@/components/templates/icons/OrientationIcon';
import ARIcon from '@/components/templates/icons/ARIcon';
import { PodFrameBuilderElement } from '@/types/pod-frame-builder';
import SizeIcon from '@/components/templates/icons/SizeIcon';

const ProductBaseV2 = ({ product, handle, shopMetafields, colorCollection }: RouteProps) => {
  // Load reviews.io scripts
  useScript('https://widget.reviews.io/polaris/build.js');
  useSetupArtisticUrls(product);

  const isPod = useFeatureFlagEnabled('pod') ?? true;

  const { type } = useSelector((state: RootState) => state.product);
  const partner = getPartner();
  const isPartner = isPartnerMatch();
  const isPartnerShoott = partner === 'shoott';
  const [shoudReInit, setShouldReInit] = useState(false);
  const { currentFrame: galleryCurrentFrame } = useSelector((state: RootState) => state.gallery);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 799 });
  const [actionTab, setActionTab] = useState<SectionTypes | null>(
    getQueryParams('style') === 'open' ? SectionTypes.STYLE : null
  );
  const uploadRef = useRef<HTMLInputElement>(null!);

  const { tiles } = useSelector((state: RootState) => state.upload);
  const artisticHandle = getArtisticHandle();
  const currentVariant = getCurrentVariant(product?.variants?.edges);
  const frameSizeInPixels = getFrameSizeFromVariant(currentVariant);

  const isGallery = type === ProductTypes.GALLERY;
  const isOrnament = isOrnamentProduct(type, product?.title);

  const navHeader = document.getElementById('fn-main-header');
  const mainContent = document.getElementById('MainContent');
  const showGalleryMobileOverlay = galleryCurrentFrame !== null && isMobile;
  const hideSizeGuide = getMetafieldV2('hide_size_guide', product?.metafields) === 'true';
  const noProductVariants = product?.variants?.edges?.length === 1;
  const currentOrientation = getMetafieldV2('gallery_orientation', product.metafields);
  const productMaterial = getMetafieldV2('material_of_product', product.metafields);
  const orientationVariants = product?.orientation_variants?.references?.edges?.map(
    (edge) => edge?.node
  );
  const matGroupTitle = getMetafieldV2('mat_group_title', product.metafields);
  const matTitleDescription = getMetafieldV2('mat_title_description', product.metafields);
  const galleryTemplateIncld = getMetafieldV2('pdp_gallery_template_included', product.metafields);
  const hasOrientationVariants = (orientationVariants?.length ?? 0) > 0;
  const showArButton = isGallery && isMobile && currentVariant?.model_file;
  const orientationNames = orientationVariants?.map((name) => ({
    title: name?.orientation_type?.value ?? '',
    link: name?.handle,
  }));
  if (currentOrientation && orientationNames) {
    orientationNames.push({
      title: currentOrientation,
      link: '',
    });
  }

  const toggleTab = (tab: SectionTypes | null) => {
    setActionTab(tab === actionTab ? null : tab);
  };

  const activateAr = () => {
    const pod = document.querySelector<PodFrameBuilderElement>('pod-frame-builder');
    if (!pod) return;

    try {
      pod.activateAr();
    } catch (error) {
      console.error('AR activation failed:', error);
    }
  };

  navHeader?.classList.toggle('gallery-header', isGallery);

  // GALLERY BOTTOM BUTTONS
  const bottomButtons: { mainButtons: Buttons[] } = useMemo(() => {
    const buttons: Buttons[] = [];
    const rightButtons: Buttons[] = [];
    const colors = colorCollection?.products?.edges?.length;

    if (isOrnament && !noProductVariants) {
      buttons.push({
        onClick: () => toggleTab?.(SectionTypes.SHAPE),
        imageComponent: <ShapeIcon />,
        content: SectionTypes.SHAPE,
        hideDesktop: true,
      });
    }

    if (product?.pdp_product_group) {
      buttons.push({
        onClick: () => toggleTab?.(SectionTypes.PRODUCT),
        imageComponent: <ProductIcon />,
        content: SectionTypes.PRODUCT,
        hideDesktop: true,
      });
    }

    if (isGallery && product?.gallery_sizes_group) {
      buttons.push({
        onClick: () => toggleTab?.(SectionTypes.GALLERYSIZE),
        imageComponent: <SizeIcon />,
        content: 'Size',
        hideDesktop: true,
      });
    } 
      
    if (colors && colors > 1) {
      buttons.push({
        onClick: () => toggleTab?.(SectionTypes.COLOR),
        imageComponent: <ColorIcon />,
        content: SectionTypes.COLOR,
        hideDesktop: true,
      });
    }

    if (product?.mat_variants || product?.mat_product_group) {
      buttons.push({
        onClick: () => toggleTab?.(SectionTypes.MAT),
        imageComponent: matGroupTitle ? <GeneralIcon /> : <FrameIcon />,
        content: `${matGroupTitle || SectionTypes.MAT}`,
        hideDesktop: true,
      });
    }

    if (hasOrientationVariants) {
      buttons.push({
        onClick: () => toggleTab?.(SectionTypes.ORIENTATION),
        imageComponent: <OrientationIcon side={currentOrientation?.toLowerCase()} />,
        content: SectionTypes.ORIENTATION,
        hideDesktop: true,
      });
    }

    if (showArButton) {
      rightButtons.push({
        onClick: activateAr,
        content: 'AR',
        imageComponent: <ARIcon />,
        tooltipText: 'View on your wall',
      });
    }

    return { mainButtons: buttons, rightButtons };
  }, [colorCollection, product?.mat_variants, actionTab, showArButton]);

  if (mainContent) {
    mainContent.dataset.prodtype = String(type);
    mainContent.dataset.handle = String(handle);
  }

  const onUploadPhoto = (e: React.MouseEvent) => {
    e.preventDefault();
    uploadRef.current?.click();
  };

  const onCloseSection = () => setActionTab(null);
  const pageName = '';

  const getProductSelector = (
    <>
      {isMobile && (
        <>
          <Selector
            onClose={onCloseSection}
            title={'Choose style'}
            open={actionTab === SectionTypes.STYLE}
          >
            <>
              <ProductCollections product={product} />
              <ProductSwatches pagePath={`/products${pageName}`} showDefaultColor />
            </>
          </Selector>

          {product?.pdp_product_group ? (
            <Selector
              onClose={onCloseSection}
              title={'Choose product'}
              open={actionTab === SectionTypes.PRODUCT}
            >
              <ProductCollectionGroup sectionTitle={SectionTypes.PRODUCT} />
            </Selector>
          ) : null}

          {isGallery ? (
            <Selector
              onClose={onCloseSection}
              title={'Choose size'}
              open={actionTab === SectionTypes.GALLERYSIZE}
            >
              <ProductGallerySizes sectionTitle={'GallerySize'} />
            </Selector>
          ) : null}

          <Selector
            onClose={onCloseSection}
            title={'Choose color'}
            open={actionTab === SectionTypes.COLOR}
          >
            <ProductImageSwatches pagePath={`/products${pageName}`} showDefaultColor />
          </Selector>
          <Selector
            onClose={onCloseSection}
            title={noProductVariants ? '' : 'Choose size'}
            open={actionTab === SectionTypes.SIZE}
            helpElement={!hideSizeGuide && <SizeGuideButton />}
          >
            <ProductVariants showMeta={false} showPrice sectionTitle={'Size'} />
            <ProductHallwaySize />
          </Selector>
          <Selector
            onClose={onCloseSection}
            title={noProductVariants ? '' : 'Choose shape'}
            open={actionTab === SectionTypes.SHAPE}
          >
            <ProductVariants showMeta={false} sectionTitle={'Shape'} />
            <ProductHallwaySize />
          </Selector>
          <S.StyledSelector
            onClose={onCloseSection}
            title={`Choose ${matGroupTitle?.toLowerCase() || 'mat'}${
              matTitleDescription ? ':' : ''
            }`}
            helpElement={matTitleDescription && matTitleDescription}
            open={actionTab === SectionTypes.MAT}
          >
            <MatImageOptions />
          </S.StyledSelector>
          <Selector
            onClose={onCloseSection}
            title={'Choose orientation'}
            open={actionTab === SectionTypes.ORIENTATION}
          >
            <OrientationOptions showTitle={false} />
          </Selector>
          <Cart>
            <FloatingCta product={product} fixed onUploadPhoto={onUploadPhoto} />
          </Cart>
        </>
      )}
      {!isMobile && (
        <div className="tmplt-product__content-wrap" style={{ zIndex: 6 }}>
          <ProductHeaderV2 />
          <ProductInfoV2
            galleryTemplateIncld={galleryTemplateIncld}
            productMaterial={productMaterial?.toLowerCase()}
          />
          <Cart>
            <S.OptionsWrapper className="tmplt-product__options-wrapper">
              <S.OptionsBlock className="fn-product-options tmplt-product__options tmplt-product__options--opened">
                {product?.pdp_product_group && <ProductCollectionGroup sectionTitle={'Product'} />}
                <ProductImageSwatches
                  sectionTitle={'Color'}
                  pagePath={`/products${pageName}`}
                  showDefaultColor
                />
                {isGallery && <ProductGallerySizes sectionTitle={'Size'} />}
                <ProductVariants sectionTitle={'Size'} />
                <ProductHallwaySize />
              </S.OptionsBlock>
              <ProductOptions />
              <OrientationOptions />
            </S.OptionsWrapper>
            <ProductCta showFloatingCta={false} />
            <div style={{ display: 'flex', flexDirection: 'column-reverse' }}>
              <ValueProps />
              <AccordionContainer>
                {isGallery && (
                  <Accordion title="About this set">
                    <GalleryDescription />
                  </Accordion>
                )}
                {isGallery && (
                  <Accordion title="Products featured">
                    <GalleryItemsDescription />
                  </Accordion>
                )}
                {!isGallery && (
                  <Accordion title="Product details">
                    <ProductDescriptionV2 />
                  </Accordion>
                )}
                <Accordion title="FAQ">
                  <ProductFaqV2 />
                </Accordion>
                {product?.pdp_accordion_custom_block ? (
                  <Accordion title={product?.pdp_accordion_custom_block?.reference?.title?.value}>
                    <ProductAccordionCustom />
                  </Accordion>
                ) : null}
                {!isPartner || isPartnerShoott ? <ProductReviewMock2 /> : null}
              </AccordionContainer>
            </div>
          </Cart>
        </div>
      )}
    </>
  );

  useEffect(() => {
    DataService.saveTiles(tiles);
    setShouldReInit(!shoudReInit);
    setTimeout(() => {
      setShouldReInit(!shoudReInit);
    }, 10);
  }, [tiles]);

  useEffect(() => {
    if (handle || artisticHandle) {
      const tilesWithoutArtistic = removeArtisticTiles(tiles);
      dispatch(setTiles(tilesWithoutArtistic));
      //window.scrollTo({ top: 0, behavior: "smooth" });
      dispatch(setShouldCrop(true));
    }
  }, [handle, artisticHandle]);

  // Track a single view_item event whenever the productVariant changes
  useEffect(() => {
    if (product) {
      TrackingService.ga4Track('view_item', product);
    }
  }, [product, currentVariant]);

  useEffect(() => {
    const pageTitle =
      product?.seo?.title?.toString() || 'Frameology: Print and Frame Photos Online';
    document.title = pageTitle;
  }, [product]);

  const [frameBuilderHeight, setFrameBuilderHeight] = useState<number>(0);
  const frameBuilderRef = useRef<HTMLDivElement>(null);

  const editorialSection = product?.editorial_section?.reference;
  useEffect(() => {
    if (!frameBuilderRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setFrameBuilderHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(frameBuilderRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <>
      {!isPod && (
        <>
          <Editor />
          <Resolution />
          <GalleryMobileOverlay
            frameBuilderHeight={frameBuilderHeight}
            open={showGalleryMobileOverlay}
          />
        </>
      )}
      {!hideSizeGuide && <SizeGuide />}
      <S.ProducWrapper ref={frameBuilderRef} isGallery={isGallery}>
        <BuilderWrapper className="tmplt-product">
          <S.MediaWrapper
            className="tmplt-product__media-wrapper"
            data-prodtype={type}
            data-handle={handle}
            isGallery={isGallery}
          >
            {isPod && (
              <FrameBuilderPodStyled
                product={product}
                shopMetafields={shopMetafields}
                frameSizes={frameSizeInPixels}
                variant={currentVariant}
                setActionTab={setActionTab}
                textARButton={isMobile ? 'AR' : 'View in your room'}
                colorCollection={colorCollection}
                frameBuilderHeight={frameBuilderHeight}
                actionTab={actionTab}
              />
            )}
            {!isPod && (
              <FrameBuilderStyled
                product={product}
                shopMetafields={shopMetafields}
                frameSizes={frameSizeInPixels}
                variant={currentVariant}
                setActionTab={setActionTab}
                textARButton={isMobile ? 'AR' : 'View in your room'}
                colorCollection={colorCollection}
                frameBuilderHeight={frameBuilderHeight}
                actionTab={actionTab}
              />
            )}
          </S.MediaWrapper>
          {getProductSelector}
        </BuilderWrapper>
      </S.ProducWrapper>
      {isGallery && isMobile ? (
        <>
          <ProductHeaderV2 />
          <ProductInfoV2
            galleryTemplateIncld={galleryTemplateIncld}
            productMaterial={productMaterial?.toLowerCase()}
          />
        </>
      ) : null}
      {isMobile ? (
        <>
          {isGallery ? (
            <S.GalleryBottomButtons>
              <BottomButtons className="frame-builder-bottom-container" buttons={bottomButtons} />
            </S.GalleryBottomButtons>
          ) : null}
          <ValueProps />
          <AccordionContainer>
            {isGallery && (
              <>
                <Accordion title="About this set">
                  <GalleryDescription />
                </Accordion>
                <Accordion title="Products featured">
                  <GalleryItemsDescription />
                </Accordion>
              </>
            )}
            {!isGallery && (
              <Accordion title="Product details">
                <ProductDescriptionV2 />
              </Accordion>
            )}
            <Accordion title="FAQ">
              <ProductFaqV2 />
            </Accordion>
            {product?.pdp_accordion_custom_block ? (
              <Accordion title={product?.pdp_accordion_custom_block?.reference?.title?.value}>
                <ProductAccordionCustom />
              </Accordion>
            ) : null}
            {!isPartner || isPartnerShoott ? <ProductReviewMock2 /> : null}
          </AccordionContainer>
        </>
      ) : null}
      {!isPartner ? (
        <>
          {editorialSection && <ProductAttributionDescriptionV2 />}

          <ProductMedia />
          {!isGallery && <RelatedProducts />}
          <Reviews />
        </>
      ) : null}
      {isPartnerShoott ? <Reviews /> : null}
      <Performance product={product} />
    </>
  );
};
export default ProductBaseV2;
